/* src/modules/tutors/selectors.js */

import { createSelector } from 'reselect';
import {
  get,
  find,
  findLastIndex,
  groupBy,
  isEmpty,
  last,
  map,
  slice,
  toLower,
} from 'lodash';

import { getMoment } from '../../util/dates';
import { formatReview, reviewsData } from '../../util/misc';
import { loadTexts } from '../../config/texts';

const TEXT_KEY = 'tutors';
const days = 'MTWRFSU';

export const getTutors = createSelector(
  state => state.tutors.main.data,
  state => state.appState.locale,
  (tutors, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    const { table } = texts;
    return map(Object.values(tutors), t => {
      const { _id, user, ratings, statistics, isAvailable } = t;
      const ratingsSum = ratings.reduce((acc, x) => acc + x.total / x.count, 0);
      return {
        _id,
        [table.name]: `${user.first} ${user.last}`,
        [table.rating]: (ratingsSum / ratings.length).toFixed(2),
        [table.minutes]: statistics.lessonHistory.minCount,
        [table.completed]: statistics.lessonHistory.count,
        [table.online]: isAvailable ? 'Yes' : '',
      };
    });
  },
);

export const fetchTutorsState = createSelector(
  state => state.tutors.main.loading,
  state => state.tutors.main.error,
  (loading, error) => ({ loading, error }),
);

export const getTutorsListRange = createSelector(
  state => state.tutors.browse,
  ({ start, end, count }) => ({ start, end, count }),
);

export const getSelected = createSelector(
  state => state.tutors.main.data,
  state => state.tutors.single.data,
  (tutors, tutor) => {
    const def = {
      _id: '',
      name: 'name',
      email: 'email',
      phone: 'phone',
      educotion: [],
      empty: true,
      available: false,
    };
    if (isEmpty(tutor)) return def;
    const {
      _id,
      user: { first, last: l, avatar, education, email, phone, howDidYouHear },
      bio,
    } = tutor;
    const { isAvailable } = tutors[_id] || { isAvailable: false };
    return {
      _id,
      name: `${first} ${l}`,
      email: email || def[email],
      phone: phone || def[phone],
      avatar,
      education,
      bio,
      available: isAvailable,
      hearAboutUs: howDidYouHear,
    };
  },
);

export const fetchTutorState = createSelector(
  state => state.tutors,
  tutors => ({ loading: false, error: '' }),
);

export const getLessons = createSelector(
  state => state.tutors.single.data,
  data => {
    if (isEmpty(data)) return [];
    return map(
      data.lessons,
      ({ _id, course, group: { studentMembers }, current }) => {
        const [
          {
            student: {
              user: { first, last },
            },
          },
        ] = studentMembers;
        const d = {
          _id,
          student: `${first} ${last}`,
          date: getMoment(current.startTime.timestamp).format('MMM D, Y'),
        };

        if (!course) d.course = 'Unknown Course';
        else d.course = `${course.subject} ${course.number}`;

        return d;
      },
    );
  },
);

export const getLessonInfo = createSelector(
  state => state.lessons.single.data,
  single => {
    const def = {
      _id: '',
      tutor: 'tutor',
      student: 'student',
      course: 'course',
      status: 'status',
      location: 'location',
      address: 'address',
      date: 'date',
      time: 'time',
      price: 'price',
      studentFee: '0',
      tutorFee: '0',
      lastUpdated: 'last updated',
      charges: [],
    };
    if (isEmpty(single)) return def;
    const {
      _id,
      studentFee,
      tutorFee,
      location,
      timeline,
      charges,
      group,
      course,
      current,
      status,
      price,
    } = single;
    const tutor =
      group && group.tutor
        ? `${group.tutor.user.first} ${group.tutor.user.last}`
        : 'tutor';
    const tutorID = group && group.tutor ? group.tutor._id : '';
    let student = 'student';
    let studentID = '';
    if (group && group.studentMembers) {
      const [
        {
          student: {
            _id: studentID,
            user: { first, last: sl },
          },
        },
      ] = group.studentMembers;
      student = `${first} ${sl}`;
    }
    let time = 'time';
    let date = 'date';
    if (current) {
      const t = getMoment(current.startTime.timestamp);
      const duration = t.clone();
      duration.add(current.timeslots.length * 30, 'minutes');
      date = t.format('MMM D, Y');
      time = `${t.format('HH:mm')} - ${duration.format('HH:mm')}`;
    }
    const c = course ? `${course.subject} ${course.number}` : 'course';
    const lastUpdated = getMoment(last(timeline).date.timestamp);
    return {
      _id,
      tutor,
      tutorID,
      student,
      studentID,
      course: c,
      status,
      location: get(location, 'name') || 'location',
      address: get(location, 'address') || 'address',
      date,
      time,
      price,
      studentFee: (studentFee / 100).toFixed(2),
      tutorFee: (tutorFee / 100).toFixed(2),
      lastUpdated: `${lastUpdated.format('MMM D, Y')} at ${lastUpdated.format(
        'HH:mm',
      )}`,
      canceled: (timeline.find(t => t.type === 'CANCEL') || { emitter: '' })
        .emitter,
      charges,
    };
  },
);

export const getPaymentInfo = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    if (isEmpty(tutor)) return [];
    return [
      {
        title: texts.title.stripe,
        value: tutor.paymentSet
          ? texts.title.connected
          : texts.title.not_connected,
      },
    ];
  },
);

export const getCourses = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    const { fields } = texts;
    if (isEmpty(tutor)) return [];
    return map(tutor.courses, ({ course, status, price, active }) => ({
      _id: course._id,
      [fields.course]: `${course.subject} ${course.number}`,
      [fields.institution]: course.university.name,
      [fields.price]: price === -1 ? fields.not_set : (price / 100).toFixed(2),
      [fields.status]: status,
      [fields.active]: active ? fields.yes : fields.no,
    }));
  },
);

export const getNumApproved = createSelector(
  state => state.tutors.single.data,
  tutor => {
    if (isEmpty(tutor)) return 0;
    return tutor.courses.reduce((acc, t) => {
      if (t.status === 'APPROVED' && t.price !== -1) return acc + 1;
      return acc;
    }, 0);
  },
);

export const getPolicies = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    const def = {
      studentRestrictionPolicy: [
        {
          title: texts.policies.student_restriction,
          value: texts.policies.disabled,
        },
      ],
      cancelationPolicy: [
        { title: texts.policies.min_hour, value: '0 hours' },
        { title: texts.policies.penalty, value: '0%' },
      ],
    };
    if (isEmpty(tutor)) return def;
    const {
      policies: { studentRestriction, cancelation },
    } = tutor;
    const sEnabled = studentRestriction.enabled
      ? texts.fields.yes
      : texts.fields.no;
    return {
      studentRestrictionPolicy: [
        { title: texts.policies.enabled, value: sEnabled },
      ],
      cancelationPolicy: [
        {
          title: texts.policies.enabled,
          value: cancelation.enabled ? texts.fields.yes : texts.fields.no,
        },
        { title: texts.policies.min_hour, value: `${cancelation.time} hours` },
        { title: texts.policies.penalty, value: `${cancelation.penalty}%` },
      ],
    };
  },
);

export const getLocations = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    if (isEmpty(tutor)) return [];
    return map(tutor.locations.filter(Boolean), l => ({
      _id: l._id,
      [texts.fields.name]: l.name,
      [texts.fields.address]: l.address,
    }));
  },
);

export const getSchedules = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    if (isEmpty(tutor)) return [];
    return map(tutor.schedules.filter(Boolean), s => {
      // get start and end times,
      const {
        pattern: { shape, repeat },
        timeblocks,
      } = s;
      const start = getMoment(s.startDate.timestamp);
      const lastInShape = findLastIndex(shape, a => a);
      const end = start.clone();
      end.add(7 * repeat + lastInShape, 'days');
      // shape
      const i = (parseInt(start.format('d'), 10) + 6) % 7;
      const standardShape = [
        ...slice(shape, 7 - i, 7),
        ...slice(shape, 0, 7 - i),
      ];
      let time = '';
      if (!isEmpty(timeblocks)) {
        const startTime = getMoment(timeblocks[0].startTime.timestamp);
        const endTime = getMoment(timeblocks[0].endTime.timestamp);
        time = `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`;
      }
      return {
        _id: s._id,
        [texts.fields.start_time]: start.format('MMM D, Y'),
        [texts.fields.end_time]: end.format('MMM D, Y'),
        [days]: standardShape.reduce(
          (acc, x, j) => `${acc}${x ? days[j] : ''}`,
          '',
        ),
        [texts.fields.time]: time,
      };
    });
  },
);

export const getReviewsList = createSelector(
  state => state.tutors.single.data,
  tutor => {
    if (isEmpty(tutor)) return [];

    const reviews = tutor.reviews.map(r => formatReview(r));
    reviews.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    return reviews;
  },
);

export const getFeedbackList = createSelector(
  state => state.tutors.single.data,
  tutor => {
    if (isEmpty(tutor)) return [];
    const reviews = tutor.reviewsGiven.map(r => formatReview(r, 'to'));
    reviews.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    return reviews;
  },
);

export const getNotesList = createSelector(
  state => state.tutors.single.data,
  tutor => {
    if (isEmpty(tutor)) return [];
    const notes = map(tutor.user.notes, n => {
      const {
        _id,
        admin: { user },
        body,
        createdAt,
      } = n;
      const date = getMoment(createdAt.timestamp);
      return {
        _id,
        author: `${user.first} ${user.last}`,
        body,
        date,
      };
    });
    notes.sort((a, b) => a.date.valueOf() - b.date.valueOf());
    return map(notes, ({ date, ...rest }) => ({
      ...rest,
      date: date.format('MMM D, Y'),
    }));
  },
);

export const getReviewsData = createSelector(
  state => state.tutors.main.data,
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutors, tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);
    const def = [
      { title: texts.reviews.received, value: '0' },
      { title: texts.reviews.knowledge, value: '0' },
      { title: texts.reviews.helpfulness, value: '0' },
      { title: texts.reviews.organization, value: '0' },
      { title: texts.reviews.rating, value: '0' },
    ];
    if (isEmpty(tutor) || isEmpty(tutors[tutor._id])) return def;

    // TODO: calculate reviews
    const reviews = tutor.reviews.map(r => formatReview(r));

    const data = reviewsData(reviews);

    const out = [
      { title: texts.reviews.received, value: `${data.count}` },
      ...Object.keys(data.averages).map(key => ({
        title: `${texts.reviews.average}: ${key}`,
        value: data.averages[key].toFixed(2),
      })),
    ];

    if (data.overall !== -1) {
      out.push({
        title: texts.reviews.rating,
        value: `${data.overall.toFixed(2)}`,
      });
    }

    return out;
  },
);

export const getAnalyticsData = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);

    if (isEmpty(tutor)) return [];
    const group = groupBy(
      tutor.lessons,
      l => l.group.studentMembers[0].student._id,
    );
    const revenue = tutor.lessons.reduce((acc, x) => {
      if (x.status === 'COMPLETED') return acc + x.price;
      return acc;
    }, 0);
    const profit = tutor.lessons.reduce((acc, x) => {
      if (x.status === 'COMPLETED' && x.price > 0) {
        const sub = acc + x.tutorFee + x.studentFee;
        const stripeFee = 30 + 0.029 * sub;
        return sub - stripeFee;
      }
      return acc;
    }, 0);
    return [
      {
        title: texts.analytics.students_taught,
        value: `${Object.keys(group).length}`,
      },
      {
        title: texts.analytics.revenue,
        value: `${(revenue / 100).toFixed(2)} CAD`,
      },
      {
        title: texts.analytics.profit,
        value: `${(profit / 100).toFixed(2)} CAD`,
      },
    ];
  },
);

export const getAverages = createSelector(
  state => state.tutors.single.data,
  state => state.appState.locale,
  (tutor, locale) => {
    const texts = loadTexts(TEXT_KEY, locale);

    const def = [
      { title: texts.analytics.hourly_rate, value: '0' },
      {
        title: texts.analytics.response_rate,
        value: `0 ${texts.analytics.hours}`,
      },
      {
        title: texts.analytics.message_rate,
        value: `0 ${texts.analytics.hours}`,
      },
    ];
    if (isEmpty(tutor)) return def;
    const {
      lessons,
      statistics: { chatResponseTime, lessonResponseTime },
    } = tutor;
    const totalPrice = lessons.reduce((acc, x) => {
      if (x.status === 'COMPLETED') return acc + x.price;
      return acc;
    }, 0);
    const totalDuration = lessons.reduce((acc, x) => {
      if (x.status === 'COMPLETED')
        return acc + x.current.timeslots.length * 0.5;
      return acc;
    }, 0);
    const lessonRespRate =
      lessonResponseTime.minCount / lessonResponseTime.count;
    const chatRespRate = chatResponseTime.minCount / chatResponseTime.count;
    const hrRateValue =
      totalDuration === 0
        ? 'N/A'
        : (totalPrice / totalDuration / 100).toFixed(2);
    const LRRValue =
      lessonResponseTime.count === 0 ? 'N/A' : (lessonRespRate / 60).toFixed(2);
    const CRRValue =
      chatResponseTime.count === 0 ? 'N/A' : (chatRespRate / 60).toFixed(2);
    return [
      { title: texts.analytics.hourly_rate, value: hrRateValue },
      {
        title: texts.analytics.response_rate,
        value: `${LRRValue} ${texts.analytics.hours}`,
      },
      {
        title: texts.analytics.message_rate,
        value: `${CRRValue} ${texts.analytics.hours}`,
      },
    ];
  },
);

export const getPaymentErrors = createSelector(
  state => state.tutors.single.data,
  tutor => {
    if (isEmpty(tutor)) return [];
    const errors = map(tutor.user.paymentErrors, p => ({
      title: getMoment(p.createdAt.timestamp),
      value: p.code,
    }));
    errors.sort((a, b) => a.title.valueOf() - b.title.valueOf());
    return map(errors, ({ title, ...rest }) => ({
      ...rest,
      title: title.format('MMM D, YYYY'),
    }));
  },
);



// WEBPACK FOOTER //
// ./src/modules/tutors/selectors.js